import {
  CheckIcon,
  Flex,
  Form,
  FormSearchSelection,
  FormSelection,
  FormTextarea,
  FormTextField,
  type OptionSelectionProps,
  Text,
} from '@shared/components';
import { isThaiTitle } from '@shared/utils';
import { FormProvider, type UseFormReturn } from 'react-hook-form';

import { MIDDLE_NAME_HELPER_TEXT } from '../../constants';
import {
  type AlternativeRegistrationFormOutput,
  type AlternativeRegistrationFormInput,
} from '../../schemas';
import { FormImageInput, type FormImageInputProps } from '../form-image-input';

interface AlternativeRegistrationFormField {
  name: string;
}

export interface AlternativeRegistrationFormProps {
  form: UseFormReturn<AlternativeRegistrationFormInput, unknown, AlternativeRegistrationFormOutput>;
  onSubmit?: (data: AlternativeRegistrationFormOutput) => void;
  slotProps: {
    nameTitleField: AlternativeRegistrationFormField & { options: OptionSelectionProps[] };
    firstNameField: AlternativeRegistrationFormField;
    middleNameField: AlternativeRegistrationFormField;
    lastNameField: AlternativeRegistrationFormField;
    documentTypeField: AlternativeRegistrationFormField & { options: OptionSelectionProps[] };
    documentImageField: AlternativeRegistrationFormField & Pick<FormImageInputProps, 'onClick'>;
    causeField: AlternativeRegistrationFormField;
  };
}

export default function AlternativeRegistrationForm(
  props: Readonly<AlternativeRegistrationFormProps>,
) {
  const { form, onSubmit, slotProps } = props;
  const {
    nameTitleField,
    firstNameField,
    middleNameField,
    lastNameField,
    documentTypeField,
    documentImageField,
    causeField,
  } = slotProps;

  const { getValues, setValue } = form;

  return (
    <FormProvider {...form}>
      <Form onSubmit={onSubmit ? form.handleSubmit(onSubmit) : undefined}>
        <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
          {/* Name Title */}
          <FormSearchSelection
            label='คำนำหน้า'
            placeholder='เลือกคำนำหน้า'
            required
            itemComponent={(componentsProps) => {
              const isSelected = getValues('nameTitle') === componentsProps.option.value;
              return (
                <Flex sx={{ width: '100%', justifyContent: 'space-between' }}>
                  <Text>{componentsProps.option.label}</Text>
                  {isSelected ? <CheckIcon color='primary' sx={{ fontSize: '24px' }} /> : null}
                </Flex>
              );
            }}
            // The form carries the title display text (string), while the
            // Autocomplete works with { label, value } options.
            isOptionEqualToValue={(option, value) =>
              option.value === (typeof value === 'string' ? value : value?.value)
            }
            onChange={(_event, item) => {
              const value = typeof item?.value === 'string' ? item.value : null;
              setValue('nameTitle', value !== null && isThaiTitle(value) ? value : null, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
            sx={{
              flex: 1,
            }}
            {...nameTitleField}
          />

          {/* First Name, Middle Name & Last Name */}
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(4),
              flexDirection: 'column',
              [theme.breakpoints.up('tablet')]: {
                flexDirection: 'row',
              },
            })}
          >
            <FormTextField
              label='ชื่อ'
              placeholder='กรอกชื่อ'
              required
              sx={{ flex: 1 }}
              {...firstNameField}
            />
            <FormTextField
              label='ชื่อกลาง'
              placeholder='กรอกชื่อกลาง'
              helperText={MIDDLE_NAME_HELPER_TEXT}
              sx={{ flex: 1 }}
              {...middleNameField}
            />
            <FormTextField
              label='นามสกุล'
              placeholder='กรอกนามสกุล'
              required
              sx={{ flex: 1 }}
              {...lastNameField}
            />
          </Flex>

          {/* Document Type */}
          <FormSelection
            label='ประเภทเอกสาร'
            placeholder='เลือกประเภทเอกสาร'
            required
            itemComponent={(props) => {
              const isSelected = getValues('documentType') === props.option.value;
              return (
                <Flex sx={{ width: '100%', justifyContent: 'space-between' }}>
                  <Text>{props.option.label}</Text>
                  {isSelected ? <CheckIcon color='primary' sx={{ fontSize: '24px' }} /> : null}
                </Flex>
              );
            }}
            {...documentTypeField}
          />

          {/* Document image */}
          <FormImageInput label='ภาพถ่ายเอกสาร' required {...documentImageField} />

          {/* Cause */}
          <FormTextarea
            label='สาเหตุ'
            required
            slotProps={{
              htmlInput: {
                sx: {
                  resize: 'vertical',
                },
              },
            }}
            {...causeField}
          />
        </Flex>
      </Form>
    </FormProvider>
  );
}
